import type { LocaleMessageSchema } from '../types';

export default {
  Common: {
    loading: '加载中...',
  },
  errors: {
    session_create_error: '创建会话失败。\n请重试。',
    session_continue_error:
      "无法加载您的会话。请重试。如果问题仍然存在，请联系支持：service{'@'}theartisan.ai。",
    kiosk_not_found: '加载自助机失败。\n请重试。',

    art_style_missing: '此自助机尚未添加\n任何艺术风格',
    art_style_fetch_error: '加载艺术风格失败',
    art_style_preload_error: '加载艺术风格预览失败',

    face_detection_server_error: '处理照片失败。请重试。',
    face_detection_preload_error: '预加载检测到的面部时出错',
    face_detection_no_faces: '未检测到面部。请尝试拍摄另一张照片。',

    photo_webcam_not_allowed: '摄像头权限被拒绝',
    photo_webcam_not_found: '未找到摄像头。请检查是否已连接到自助机。',
    photo_webcam_hardware_error: '摄像头忙碌或无法使用。\n请重试或寻求帮助。',
    photo_webcam_photo_failed: '使用摄像头拍照失败',

    photo_phone_missing_session_id: '找不到您的会话。\n请重新扫描二维码。',
    photo_phone_missing_kiosk_slug:
      "在您的会话中未找到自助机。如果问题仍然存在，请联系支持：service{'@'}theartisan.ai。",
    photo_phone_limit_exceeded: '选择的照片太多',
    photo_phone_qr_error: '生成照片上传二维码失败',

    photo_file_unsupported_type: '不支持的文件类型',
    photo_file_cannot_process: '无法处理此照片。\n请尝试其他照片。',
    photo_file_cannot_process_heic: '无法处理HEIC照片。\n请尝试其他照片。',

    portrait_generation_error: '生成您的肖像时出错。请重试。',
    portrait_generation_faces_not_selected: '未选择用于生成肖像的面部',
    portrait_generation_style_not_selected: '未选择用于生成肖像的艺术风格',
    portrait_generation_over_limit: '生成尝试次数过多',

    portraits_download_session_error:
      "找不到您的会话。请检查您输入的代码。如果问题仍然存在，请联系支持：service{'@'}theartisan.ai。",
    portraits_download_kiosk_error:
      "在您的会话中未找到自助机。如果问题仍然存在，请联系支持：service{'@'}theartisan.ai。",
    portraits_download_portraits_error:
      "在您的会话中未找到肖像。如果问题仍然存在，请联系支持：service{'@'}theartisan.ai。",

    payment_partner_mode_missing_stripe_account:
      '合作伙伴支付模式需要Stripe Connect账户。请配置合作伙伴的Stripe账户，或切换到平台支付模式。',

    api_error_missing_session_id: '未找到会话',
    api_error_email_code_validation: '支付代码验证失败。请重试。',
  },

  /**
   * Global components
   */

  OverlayError: {
    buttons: {
      close: '关闭',
      restart: '重新开始',
      retry: '重试',
    },
  },
  Logo: {
    startOver: '重新开始',
  },
  Copyright: {
    copyright: 'powered by TheArtisan.ai',
    distribution: '分销商',
    terms: '在此设备上拍照即表示您接受我们的政策和条款。如需查看，请',
    termsLink: '点击这里',
  },
  IdlePopup: {
    title: '您还在吗？',
    caption: '如果您不活动，您的会话将结束。',
    buttonContinue: '是的，我还在',
    buttonRestart: '不，重新开始',
  },
  GDPRConsent: {
    title: '需要同意',
    consentText:
      '我们需要您的同意来处理您的面部特征（生物识别数据）以创建艺术转换。只有年满16岁（或有父母同意）才能继续。',
    buttonConsent: '我同意',
    buttonDecline: '我不同意',
    privacyPolicyLink: '查看隐私政策',
    declineMessage: '未经同意无法继续',
  },
  StepProgressIndicator: {
    photoShort: '照片',
    photoLong: '拍照',
    facesShort: '面部',
    facesLong: '选择面部',
    groupPortraitsShort: '合影',
    groupPortraitsLong: '合影肖像',
    portraitsShort: '肖像',
    portraitsLong: '选择肖像',
    printShort: '打印',
    printLong: '支付并打印',
    sendToPickupShort: '取件',
    sendToPickupLong: '发送至取件处',
  },
  UploadedPhotosSidebar: {
    title: '您的照片',
    count: '+{extraPhotoCount} 更多',
  },
  NumericKeyboard: {
    invalidPin: 'PIN码无效',
    clear: '清除',
    enter: '确认',
  },
  OutOfService: {
    message: '您的合同似乎已暂停或过期。\n请联系您的分销商获取帮助。',
  },

  /**
   * Steps
   */

  Home: {
    title: '几秒钟内生成您的肖像！',
    button: '立即体验！',
  },
  ArtStyle: {
    title: '选择您的风格',
  },
  PhotoFromWebcam: {
    titleGetReady: '准备拍照',
    titleAttention: '看镜头！',
    titleReview: '您喜欢这张照片吗？',
    titleUpload: '正在上传您的照片...',
    buttonPrimaryPhoto: '拍照',
    buttonPrimaryUse: '是的，使用这张照片',
    buttonPrimaryRetake: '重拍',
    buttonSecondaryUpload: '从手机上传',
    buttonSecondaryRetake: '不，重拍（还剩1次机会）',
    buttonSecondaryUseFirst: '不，使用第一张照片',
    titleCameraError: '摄像头不可用',
    buttonRetryCamera: '重试摄像头',
  },
  PhotoPromptFromPhone: {
    title: '从手机上传照片？',
    caption: '给朋友和家人一个惊喜——\n上传他们的照片来创建肖像。',
    buttonUpload: '是的，从手机上传',
    buttonSkip: '不，跳过',
  },
  PhotoFromPhone: {
    title: '从手机上传照片',
    step1: '用手机扫描二维码',
    step2: '选择照片',
    step3: '等待上传',
    waiting: '等待您的上传...',
    done: '上传成功',
    buttonCancel: '取消并返回',
    buttonSkip: '跳过手机上传',
    buttonContinue: '继续',
    gdprConsentAll: '我已获得照片中所有人同意处理他们的面部特征',
    gdprOver16: '照片中没有16岁以下的人',
    gdprResponsibility: '我承担GDPR合规的全部责任',
    gdprAgreeToPrefix: '我同意',
    gdprPrivacyPolicyLink: '隐私政策',
    gdprAgreeToSuffix: '上传照片的条款',
    gdprCheckboxesRequired: '请勾选以下所有选项以继续',
    buttonSkipKioskPhoto: '跳过自助机照片',
    buttonTakeWebcamPhoto: '使用摄像头拍照',
  },
  FacesIndividual: {
    title: '选择谁将出现在肖像中？',
    buttonConfirm: '确认并继续',
    genderMale: '男性',
    genderFemale: '女性',
    genderNonBinary: '非二元',
    age0to2: '0-2',
    age3to9: '3-9',
    age10to19: '10-19',
    age20to29: '20-29',
    age30to39: '30-39',
    age40to49: '40-49',
    age50to59: '50-59',
    age60to69: '60-69',
    age70plus: '70+',
    raceWhite: '白人',
    raceBlack: '黑人',
    raceEastAsian: '东亚人',
    raceSoutheastAsian: '东南亚人',
    raceIndian: '印度人',
    raceLatino: '拉丁裔',
    raceMena: '中东人',
  },
  Portraits: {
    title: '选择您的肖像！',
    buttonNext: '下一步：打印已选',
    buttonNextGroupOnly: '仅继续使用合影肖像',
    galleryScrollBack: '上一组肖像',
    galleryScrollForward: '下一组肖像',
    filterLabel: '显示',
    filterAll: '全部',
    filterGroup: '合影',
    filterIndividual: '单人',
    stageKindGroup: '合影肖像',
    stageKindIndividual: '肖像',
    stagePosition: '第 {index} / {count} 张',
    stageSelect: '选择',
    stageSelected: '已选择 ✓',
    buttonMore: '生成更多肖像（剩余 {count} 次）',
    popupTitle: '您想完成订单吗？',
    popupGroupSelected: '已选合影肖像：{count}（{price}）',
    popupIndividualSelected: '已选个人肖像：{count}（{price}）',
    popupTotalSum: '总计：{sum}',
    popupButtonNo: '不，返回',
    popupButtonYes: '是的，打印并支付！',
    error: '哎呀！无法生成此肖像。',
    placeholderGenerating: '正在生成您的肖像...',
    placeholderLoading: '正在加载您的肖像...',
    groupPlaceholder: '您的合影肖像将显示在这里',
    buttonRetry: '重试',
    buttonErrorDetails: '错误详情',
    retrying: '重试中...',
    maxRetriesReached: '已达最大重试次数',
    retryAttempts: '重试次数：{count}',
    printingLockedPopupTitle: '请呼叫最近的工作人员来打印您的肖像。',
    generationPendingTitle: '部分肖像仍在生成中',
    generationPendingCaption:
      '还有 {count} 张肖像正在生成。是否等待查看全部肖像？',
    generationPendingWait: '等待全部肖像',
    generationPendingSendReady: '发送已完成的 {count} 张肖像',
    printingLockedPopupCaption: '在没有工作人员在场时，自助机打印功能不可用。',
  },
  PortraitsGroup: {
    buttonMore: '生成更多肖像',
  },
  PrintLock: {
    lockTitle: '锁定打印',
    unlockTitle: '解锁打印',
  },
  PaymentType: {
    title: '选择支付方式',
    optionCardTitle: '银行卡',
    optionCardCaption: 'Visa、Mastercard及10+支持的卡',
    optionVoucherTitle: '预付肖像',
    optionVoucherCaption: '已包含在门票中 — 请联系工作人员',
  },
  PaymentTerminal: {
    title: '请按照支付终端上的说明操作',
    titlePaymentSuccess: '正在打印您的肖像...',
    titlePaymentError: '支付失败',
    titlePrintError1: '部分肖像打印失败。',
    titlePrintError2: '请点击下方"重新打印"重试。',
    buttonRetryPrinting: '重新打印',
    buttonRetry: '重试',
    buttonCancel: '取消',
    buttonExitSession: '重新开始',
    buttonBack: '返回',
    total: '总计：',
    captionPayment: '等待您的付款...',
    captionPrinting: '付款成功',
    captionPaymentError: '出现问题。请重试或重新开始。',
    captionPaymentFinal: '支付未成功。请向工作人员求助。',
    failureCaptions: {
      notEnoughFunds: '卡内余额不足。请尝试其他银行卡。',
      expiredCard: '此卡已过期。请尝试其他银行卡。',
      cardDeclined: '此卡被拒绝。请尝试其他银行卡。',
      cardError: '此卡无法在此使用。请尝试其他银行卡。',
      processingError: '支付未完成。请使用同一张卡重试。',
      readerError: '读卡器不可用。请向工作人员求助。',
      timeout: '支付超时。请重试。',
    },
    errorMessage: '抱歉，我们无法处理您的付款',
    popupCancelledTitle: '支付已取消',
    popupCancelledCaption: '您想重试还是重新开始？',
    popupButtonContinue: '重试',
  },
  PaymentVoucher: {
    title: '请呼叫工作人员协助优惠券支付',
    buttonBack: '返回',
    pinTitle: '使用优惠券支付',
  },
  Printing: {
    title: '正在打印您的肖像...',
    titleError: '抱歉，部分肖像打印失败',
    buttonPrintAgain: '重新打印',
    buttonContinueWithoutPrint: '不打印，继续',
    error: '哎呀！无法打印此肖像。',
    errorPartial: '已打印 {printed}/{total}',
    retrying: '正在重试…',
    waiting: '正在等待打印机…',
  },
  PrintFailed: {
    title: '很抱歉，我们无法打印您的照片',
    titlePartial: '{total} 张照片中已打印 {printed} 张',
    caption: '您可以开始新的会话并生成新的肖像。',
    captionRefunded: '我们已将 {amount} 退回您的银行卡。',
    captionNotCharged: '我们未向您收取 {amount}。',
    captionRefundHelp: '工作人员将协助您办理退款。',
  },
  Success: {
    title: '谢谢！',
    caption: '您的画作已打印。请到前台领取。',
    buttonStartAgain: '重新开始',
  },
  GoPhotoScan: {
    title: '扫描您的PhotoPass',
    subtitle: '将您的PhotoPass二维码对准扫描器',
    invalidCode: '代码格式无效。请重新扫描。',
    failedToRegister: '注册代码失败。请重试。',
    error: '发生错误。请重试。',
  },

  /**
   * Customer-facing components
   */

  CustomerPhotoFromPhone: {
    title: '从相册上传照片',
    buttonSelect: '选择照片',
    maxCount: '最多 {count} 张照片',
    uploading: '上传中...',
    uploadError: '上传失败。请检查您的网络连接后重试。',
    buttonRetry: '重试',
    successTitle: '上传成功',
    successCaption: '请返回自助机并按照屏幕上的说明操作。',
  },
  CustomerPortraitsDownload: {
    title: '输入代码获取您的肖像',
    buttonSubmit: '提交',
    error: '此代码似乎不正确。请检查照片上印刷的 8 位代码，然后重试。',
    errors: {
      wrongFormat: '此代码似乎不正确。请检查照片上印刷的 8 位代码，然后重试。',
      notFound: '抱歉，未找到肖像。请检查代码 - 它印在肖像正面的左下角。',
      serverError: '抱歉，目前无法获取您的肖像。请稍后再试。',
    },
  },
  CustomerSessionPass: {
    tabs: {
      photos: '照片',
      pass: '二维码通行证',
    },
    eyebrow: '您的拍摄通行证',
    title: '请保持此页面打开',
    description:
      '在购买终端扫描下方方形二维码。您的肖像可在“照片”选项卡中查看。',
    descriptionNothingPrinted: '在购买站扫描下方的方形码，选择并打印您的照片。',
    scanLabel: '在购买终端扫描',
    codeLabel: '拍摄代码',
    ready: '可以扫描',
    loading: '正在生成代码',
    unavailable: '二维码不可用',
    qrAlt: '购买终端二维码',
    defaultVenue: 'TheArtisan.ai 肖像体验',
  },
  CustomerPortraitsDownloadCode: {
    error: '哎呀！无法加载此肖像。',
    placeholder: '正在加载您的肖像...',
    imageAlt: '肖像',
    buttonDownload: '下载',
    buttonSendOrShare: '发送或分享',
    buttonRetry: '重试',
    shareTitle: '您来自 {clientName} 的肖像',
    shareText: 'Powered by TheArtisan.ai',
    states: {
      processing: '您的肖像仍在处理中，请稍后重试。',
      partial: '部分肖像或页面信息无法加载，其他可用下载仍可正常使用。',
    },
    actions: {
      enterAnotherCode: '输入其他代码',
      contactSupport: '联系支持',
      retryDownload: '重新下载',
      downloadFailed: '下载失败，请检查网络连接后重试。',
      linkCopied: '链接已复制，您可以将其粘贴到消息中。',
      shareFailed: '当前无法分享，请重试。',
    },
  },
  CustomerDigitalExtras: {
    downloadTitle: '您的照片和数字商品',
    downloadIntro: '保存照片、下载数字商品或打开二维码票。',
    photosTitle: '您的照片',
    photosIntro: '保存照片或打开二维码通行证。',
    navigation: '您的下载',
    tabs: {
      photos: '照片',
      extras: '视频',
      pass: '二维码票',
    },
    title: '您的数字商品',
    loading: '正在加载您的数字商品…',
    loadError: '无法加载文件。请检查网络连接后重试。',
    included: '已包含',
    includedOrder: '您的视频已包含在内。请保留二维码票，以便稍后打开。',
    preparingHelp: '请保存此链接或保留二维码票。您可以关闭此页面，稍后再回来。',
    reference: '参考编号：{reference}',
    retry: '重试',
    empty: '本次参观尚未添加数字商品。',
    orderTitle: '视频订单',
    showCashier: '请向收银员出示此订单和二维码票。',
    videoTotal: '视频总价',
    payAtDesk: '请在收银台付款。',
    preparing: '正在准备文件…',
    failed: '无法制作您的视频。请向场地工作人员出示下方参考编号以获取帮助。',
    ready: '可以下载',
    video: '动态照片',
    videoDescription: '让最爱的瞬间动起来，分享给亲友或发布到动态。',
    download: '下载',
    share: '分享文件',
    preparingShare: '正在加载文件…',
    shareFailed: '无法分享。请使用下载按钮保存文件。',
  },
  GoPhotoUpload: {
    waitingForPortraits: '正在完成您的肖像',
    waitingForPortraitsSubtitle: '请等待其余肖像生成完成。',
    success: '照片已上传！',
    successSubtitle: '您的照片现已在 PhotoPass 中可用。',
  },
  system: {
    reconnecting: '正在重新连接服务器…',
  },
} satisfies LocaleMessageSchema;
